"use client";

import { type ReactNode } from "react";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { useT } from "@/hooks/useT";

// Height-locked to match rendered MarketChart (header ~44px + chart 200px + p-4
// + time filters ~34px). A mismatch here shifted "Recent Trades" on chart hydrate.
function ChartSkeleton() {
  const t = useT();
  return (
    <div className="w-full min-h-[310px] bg-gray-100 dark:bg-[#18181D] rounded-[5px] animate-pulse flex items-center justify-center">
      <span className="text-gray-400 text-sm">{t("common.loadingChart")}</span>
    </div>
  );
}

const MarketChart = dynamic(() => import("./MarketChart"), {
  ssr: false,
  loading: () => <ChartSkeleton />,
});

const CryptoPriceChart = dynamic(() => import("./CryptoPriceChart"), {
  ssr: false,
  loading: () => <ChartSkeleton />,
});

const KalshiMarketChart = dynamic(() => import("./KalshiMarketChart"), {
  ssr: false,
  loading: () => <ChartSkeleton />,
});

const HyperliquidMarketChart = dynamic(() => import("./HyperliquidMarketChart"), {
  ssr: false,
  loading: () => <ChartSkeleton />,
});

/** Detect interval from slug: btc-updown-{5m|15m|1h|4h}-{epoch} → seconds */
function getSeriesInterval(slug: string): number | null {
  if (slug.match(/-daily-\d+$/)) return 86400;
  if (slug.match(/-weekly-\d+$/)) return 7 * 86400;
  if (slug.match(/-monthly-\d+$/)) return 30 * 86400;
  const match = slug.match(/-(\d+)(m|h)-\d+$/);
  if (!match) return null;
  const val = parseInt(match[1]);
  return match[2] === 'h' ? val * 3600 : val * 60;
}

/** Calculate live market slug by replacing epoch with current aligned time */
function getLiveMarketSlug(slug: string): string | null {
  const interval = getSeriesInterval(slug);
  if (!interval) return null;
  const now = Math.floor(Date.now() / 1000);
  const currentEpoch = Math.floor(now / interval) * interval;
  const base = slug.replace(/-\d+$/, '');
  const liveSlug = `${base}-${currentEpoch}`;
  return liveSlug === slug ? null : liveSlug;
}

function getSeriesSlugFromMarketSlug(slug: string): string | null {
  const match = slug.match(/^(\w+)-updown-(5m|15m|1h|4h)-\d+$/i);
  if (match) return `${match[1].toLowerCase()}-up-or-down-${match[2].toLowerCase()}`;
  const longMatch = slug.match(/^(\w+)-updown-(daily|weekly|monthly)-\d+$/i);
  if (!longMatch) return null;
  return `${longMatch[1].toLowerCase()}-up-or-down-${longMatch[2].toLowerCase()}`;
}

/** Chart wrapper that shows "Go to live market" overlay when market expires */
export function ChartWithExpiry({
  children,
  slug,
  marketEnded,
  seriesSlug,
}: {
  children: ReactNode;
  slug: string;
  marketEnded: boolean;
  seriesSlug?: string | null;
}) {
  const t = useT();
  const router = useRouter();
  const isSeriesMarket = !!seriesSlug || getSeriesInterval(slug) !== null;

  if (!marketEnded || !isSeriesMarket) return <>{children}</>;

  return (
    <div className="relative">
      <div className="opacity-40 pointer-events-none">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center">
        <button
          onClick={async () => {
            const resolvedSeriesSlug = seriesSlug || getSeriesSlugFromMarketSlug(slug);
            if (resolvedSeriesSlug) {
              try {
                const res = await fetch(`/api/polymarket/series/${resolvedSeriesSlug}`, { cache: "no-store" });
                if (res.ok) {
                  const data = await res.json();
                  const liveEvent = Array.isArray(data?.events)
                    ? data.events.find((event: any) => event && event.closed === false)
                    : null;
                  if (liveEvent?.slug) {
                    router.push(`/polymarket/event/${liveEvent.slug}`);
                    return;
                  }
                }
              } catch {
                // Fall through to the local slug calculation.
              }
            }

            const live = getLiveMarketSlug(slug);
            if (live) router.push(`/polymarket/event/${live}`);
          }}
          className="flex items-center gap-2 rounded-[5px] py-3 px-6 bg-white hover:bg-gray-100 active:scale-[0.97] transition-all font-inter-tight text-sm font-semibold text-black shadow-lg border border-gray-200"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
            <span className="relative inline-flex rounded-[5px] h-2.5 w-2.5 bg-red-500"></span>
          </span>
          {t("market.goToLiveMarket")} →
        </button>
      </div>
    </div>
  );
}

interface SourceAwareChartProps {
  source: "polymarket" | "kalshi" | "predict" | "hyperliquid";
  slug: string;
  marketEnded: boolean;
  seriesSlug?: string | null;
  // Polymarket/Predict chart props
  clobTokenIds?: string[];
  outcomes?: string[];
  conditionId?: string | null;
  // Crypto up/down
  isCryptoUpDown?: boolean;
  cryptoChartLoading?: boolean;
  priceToBeat?: number | null;
  eventStartTime?: string | null;
  endDate?: string | null;
  question?: string;
  cryptoInterval?: "5m" | "15m" | "1h" | "4h" | null;
  // Chart actions
  countdownEl?: ReactNode;
  alertAction?: ReactNode;
  relatedMarketsAction?: ReactNode;
  // Kalshi
  marketTicker?: string;
  /** Starting time window — passed through to MarketChart. */
  defaultTimeFilter?: "1H" | "24H" | "7D" | "30D" | "ALL";
}

export default function SourceAwareChart({
  source,
  slug,
  marketEnded,
  seriesSlug,
  clobTokenIds,
  outcomes,
  conditionId,
  isCryptoUpDown,
  cryptoChartLoading,
  priceToBeat,
  eventStartTime,
  endDate,
  question,
  cryptoInterval,
  countdownEl,
  alertAction,
  relatedMarketsAction,
  marketTicker,
  defaultTimeFilter,
}: SourceAwareChartProps) {
  if (source === "kalshi") {
    return (
      <KalshiMarketChart
        marketTicker={marketTicker || slug}
        leftAction={alertAction}
        defaultTimeFilter={defaultTimeFilter}
      />
    );
  }

  if (source === "hyperliquid") {
    // The per-outcome accordion / mobile detail chart shows the
    // YES/NO outcome's OWN price over time (% chance) — that's what
    // Polymarket renders for its market chart. The hero chart on the
    // event page already shows BTC price vs target via
    // CryptoPriceLevelChart (see EventClient hlPriceLevelInfo); we
    // don't duplicate it here.
    return (
      <HyperliquidMarketChart
        marketSlug={slug}
        yesLabel={outcomes?.[0]}
        noLabel={outcomes?.[1]}
        leftAction={alertAction}
        centerAction={countdownEl}
        rightAction={relatedMarketsAction}
        defaultTimeFilter={defaultTimeFilter}
      />
    );
  }

  return (
    <ChartWithExpiry slug={slug} marketEnded={marketEnded} seriesSlug={seriesSlug}>
      {isCryptoUpDown && priceToBeat != null ? (
        <CryptoPriceChart
          priceToBeat={priceToBeat}
          eventStartTime={eventStartTime || null}
          endDate={endDate ?? null}
          question={question || ""}
          conditionId={conditionId ?? null}
          marketSlug={slug}
          interval={cryptoInterval}
        />
      ) : cryptoChartLoading || (isCryptoUpDown && priceToBeat == null) ? (
        <ChartSkeleton />
      ) : (
        <MarketChart
          marketSlug={slug}
          clobTokenIds={clobTokenIds || []}
          outcomes={outcomes || []}
          conditionId={conditionId ?? null}
          marketEnded={marketEnded}
          centerAction={countdownEl}
          leftAction={alertAction}
          rightAction={relatedMarketsAction}
          defaultTimeFilter={defaultTimeFilter}
        />
      )}
    </ChartWithExpiry>
  );
}
